import '../operations.css';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Table } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { suppressionsApi } from '../../api/suppressions';
import type { SMSOptOutEntry, SuppressionEntry, SuppressionReason } from '../../api/suppressions';

const reasonStyles: Record<string, string> = {
  bounce: 'bg-red-100 text-red-800',
  complaint: 'bg-yellow-100 text-yellow-800',
  manual: 'bg-gray-100 text-gray-600',
};

const reasonLabels: Record<SuppressionReason, string> = {
  bounce: 'Bounce',
  complaint: 'Complaint',
  manual: 'Manual',
};

export function SuppressionList() {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState<SuppressionReason>('manual');
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<string | null>(null);

  const { data: entries, isLoading, isError } = useQuery({
    queryKey: ['suppressions'],
    queryFn: async () => (await suppressionsApi.getAll()).data,
  });
  const { data: phoneEntries } = useQuery({
    queryKey: ['smsOptOuts'],
    queryFn: async () => (await suppressionsApi.getSMSOptOuts()).data,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['suppressions'] });

  const addMutation = useMutation({
    mutationFn: ({ email, reason }: { email: string; reason: SuppressionReason }) =>
      suppressionsApi.create(email, reason),
    onSuccess: () => {
      setEmail('');
      invalidate();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => suppressionsApi.remove(id),
    onSuccess: invalidate,
  });
  const deletePhoneMutation = useMutation({
    mutationFn: (id: number) => suppressionsApi.removeSMSOptOut(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['smsOptOuts'] }),
  });

  const importMutation = useMutation({
    mutationFn: (payload: { file?: File; text?: string; reason: SuppressionReason }) => {
      if (payload.file) {
        return suppressionsApi.importFile(payload.file, payload.reason);
      }
      return suppressionsApi.importEmails(payload.text ?? '', payload.reason);
    },
    onSuccess: (res) => {
      setImportResult(`Imported ${res.data.imported} address${res.data.imported === 1 ? '' : 'es'} (${res.data.skipped} skipped).`);
      setImportText('');
      setImportFile(null);
      invalidate();
    },
  });

  const submitAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    addMutation.mutate({ email: email.trim(), reason });
  };

  const submitImport = () => {
    setImportResult(null);
    importMutation.mutate({ file: importFile ?? undefined, text: importText, reason: 'manual' });
  };

  const columns = [
    {
      key: 'email',
      header: 'Email',
      render: (entry: SuppressionEntry) => (
        <span className="font-medium text-gray-900">{entry.email}</span>
      ),
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (entry: SuppressionEntry) => (
        <span
          className={`inline-block px-2 py-1 rounded-full text-xs font-medium capitalize ${reasonStyles[entry.reason] ?? 'bg-gray-100 text-gray-600'}`}
        >
          {reasonLabels[entry.reason as SuppressionReason] ?? entry.reason}
        </span>
      ),
    },
    {
      key: 'source_rid',
      header: 'Source',
      render: (entry: SuppressionEntry) => (
        <span className="text-sm text-gray-500 font-mono">{entry.source_rid || '—'}</span>
      ),
    },
    {
      key: 'created_at',
      header: 'Added',
      render: (entry: SuppressionEntry) => (
        <span className="text-sm text-gray-500">
          {new Date(entry.created_at).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (entry: SuppressionEntry) => (
        <div className="flex justify-end">
          <Button
            variant="danger"
            size="sm"
            loading={deleteMutation.isPending && deleteMutation.variables === entry.id}
            onClick={() => deleteMutation.mutate(entry.id)}
          >
            Remove
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="operations-page space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="operations-eyebrow">Deliverability</p>
          <h1 className="text-2xl font-bold text-gray-900">Suppressions</h1>
          <p className="text-gray-500 mt-1">
            Bounced, dead, and complaining addresses are excluded from campaign sends
          </p>
        </div>
        <Button variant="secondary" onClick={() => { setImportResult(null); setImportOpen(true); }}>
          Import list
        </Button>
      </div>

      <Card>
        <form onSubmit={submitAdd} className="flex flex-wrap items-end gap-4 mb-4">
          <div className="flex-1 min-w-[220px]">
            <label htmlFor="suppression-email" className="block text-sm font-medium text-gray-700 mb-1">
              Email address
            </label>
            <input
              id="suppression-email"
              type="email"
              placeholder="recipient@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="suppression-reason" className="block text-sm font-medium text-gray-700 mb-1">
              Reason
            </label>
            <select
              id="suppression-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value as SuppressionReason)}
              className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="manual">Manual</option>
              <option value="bounce">Bounce</option>
              <option value="complaint">Complaint</option>
            </select>
          </div>
          <Button type="submit" loading={addMutation.isPending} disabled={!email.trim()}>
            Add suppression
          </Button>
        </form>
        {addMutation.isError && (
          <p role="alert" className="text-red-700 mb-4">
            Could not add this address. Check the email format and try again.
          </p>
        )}
        {deleteMutation.isError && (
          <p role="alert" className="text-red-700 mb-4">
            Could not remove this suppression. Refresh the page and try again.
          </p>
        )}

        {isError ? (
          <p role="alert" className="text-red-700">
            Could not load suppressions. Refresh the page to try again.
          </p>
        ) : isLoading ? (
          <div className="animate-pulse space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-100 rounded"></div>
            ))}
          </div>
        ) : (
          <Table
            columns={columns}
            data={entries ?? []}
            keyExtractor={(entry) => entry.id}
            emptyMessage="No suppressed addresses yet."
          />
        )}
      </Card>

      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">SMS opt-outs</h2>
        <p className="text-sm text-gray-500 mb-4">Numbers that replied STOP or UNSUBSCRIBE are excluded from SMS campaigns.</p>
        {(phoneEntries ?? []).length === 0 ? (
          <p className="text-sm text-gray-500">No SMS opt-outs yet.</p>
        ) : (
          <Table
            columns={[
              { key: 'phone', header: 'Phone number', render: (entry: SMSOptOutEntry) => <span className="font-medium">{entry.phone}</span> },
              { key: 'reason', header: 'Reason', render: (entry: SMSOptOutEntry) => <span className="capitalize">{entry.reason}</span> },
              { key: 'created_at', header: 'Added', render: (entry: SMSOptOutEntry) => new Date(entry.created_at).toLocaleDateString() },
              { key: 'actions', header: '', render: (entry: SMSOptOutEntry) => <div className="flex justify-end"><Button variant="danger" size="sm" loading={deletePhoneMutation.isPending && deletePhoneMutation.variables === entry.id} onClick={() => deletePhoneMutation.mutate(entry.id)}>Remove</Button></div> },
            ]}
            data={phoneEntries ?? []}
            keyExtractor={(entry) => entry.id}
            emptyMessage="No SMS opt-outs yet."
          />
        )}
        {deletePhoneMutation.isError && <p role="alert" className="text-red-700 mt-3">Could not remove this SMS opt-out.</p>}
      </Card>

      <Modal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        title="Import suppressions"
        size="lg"
      >
        <p className="text-gray-600 mb-4">
          Upload a CSV/text file, or paste a list of addresses (one per line, or
          comma-separated). Addresses already on the list are skipped.
        </p>
        <label htmlFor="suppression-import-file" className="block text-sm font-medium text-gray-700 mb-1">
          File
        </label>
        <input
          id="suppression-import-file"
          type="file"
          accept=".csv,.txt,text/csv,text/plain"
          onChange={(e) => setImportFile(e.target.files?.[0] ?? null)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
        />
        <label htmlFor="suppression-import-text" className="block text-sm font-medium text-gray-700 mb-1">
          Or paste addresses
        </label>
        <textarea
          id="suppression-import-text"
          rows={6}
          value={importText}
          onChange={(e) => setImportText(e.target.value)}
          placeholder={'a@example.com\nb@example.com, c@example.com'}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        {importResult && (
          <p role="status" className="text-green-700 mb-4">{importResult}</p>
        )}
        {importMutation.isError && (
          <p role="alert" className="text-red-700 mb-4">
            Import failed. Check the file or pasted list and try again.
          </p>
        )}
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setImportOpen(false)}>
            Close
          </Button>
          <Button
            loading={importMutation.isPending}
            disabled={!importText.trim() && !importFile}
            onClick={submitImport}
          >
            Import
          </Button>
        </div>
      </Modal>
    </div>
  );
}
